// Общие токены и хуки для анимаций (длительности, easing, scroll-reveal,
// tilt / magnetic / parallax). Если у пользователя включено
// prefers-reduced-motion — хуки превращаются в no-op.
//
// Заодно реэкспортируем Reveal/RevealStagger из components/motion/Reveal —
// чтобы где-нибудь в коде можно было писать `import { Reveal } from '@/lib/motion'`
// и не запоминать, в каком ровно файле живёт компонент.

import { useCallback, useEffect, useRef, useState } from 'react';

export { Reveal, RevealStagger } from '@/components/motion/Reveal';

// JS-зеркало CSS-переменных --mx-dur-* из index.css. Держим в синхроне руками.
export const DURATIONS = {
  ultraFast: 120,
  fast: 200,
  base: 320,
  slow: 520,
  cinematic: 900,
};

export const EASINGS = {
  // мягкий выход (наш дефолт для появления)
  out: 'cubic-bezier(0.16, 1, 0.3, 1)',
  // стандарт material — для повседневных hover/focus
  inOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  // лёгкий overshoot — там, где нужен «живой» отклик
  spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  // быстрый старт, мягкий финиш — для уходящих элементов
  in: 'cubic-bezier(0.7, 0, 0.84, 0)',
};

// --- prefers-reduced-motion ---
let _reducedMotionCache = null;

export function prefersReducedMotion() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  if (_reducedMotionCache === null) {
    _reducedMotionCache = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  return _reducedMotionCache;
}

export function useReducedMotion() {
  const [reduced, setReduced] = useState(() => prefersReducedMotion());

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => {
      _reducedMotionCache = mq.matches;
      setReduced(mq.matches);
    };
    sync();
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', sync);
      return () => mq.removeEventListener('change', sync);
    }
    // Safari fallback
    mq.addListener(sync);
    return () => mq.removeListener(sync);
  }, []);

  return reduced;
}

// --- useReveal: scroll-in анимации ---
// Один общий IntersectionObserver на всё приложение: наблюдает за элементами с
// `data-reveal` и навешивает класс `is-revealed`. Так можно делать scroll-reveal
// просто через data-атрибут — без обёрток-компонентов.
let _sharedObserver = null;
let _observedNodes = new WeakSet();

function ensureObserver() {
  if (typeof window === 'undefined') return null;
  if (_sharedObserver) return _sharedObserver;
  if (prefersReducedMotion()) {
    _sharedObserver = {
      observe: (el) => {
        if (el) el.classList.add('is-revealed');
      },
      unobserve: () => {},
      disconnect: () => {},
    };
    return _sharedObserver;
  }
  if (typeof IntersectionObserver === 'undefined') {
    _sharedObserver = {
      observe: (el) => {
        if (el) el.classList.add('is-revealed');
      },
      unobserve: () => {},
      disconnect: () => {},
    };
    return _sharedObserver;
  }
  _sharedObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          _sharedObserver.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
  );
  return _sharedObserver;
}

export function attachReveal(node) {
  if (!node || _observedNodes.has(node)) return () => {};
  const obs = ensureObserver();
  if (!obs) return () => {};
  _observedNodes.add(node);
  obs.observe(node);
  return () => {
    try {
      obs.unobserve(node);
    } catch (_) {}
  };
}

export function useReveal(options = {}) {
  const ref = useRef(null);
  const { variant = 'up', delay, stagger } = options;
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    if (variant && !node.dataset.reveal) node.dataset.reveal = variant;
    if (typeof delay === 'number') node.style.setProperty('--reveal-delay', `${delay}ms`);
    if (typeof stagger === 'number') node.style.setProperty('--reveal-stagger', `${stagger}ms`);
    return attachReveal(node);
  }, [variant, delay, stagger]);
  return ref;
}

// --- useTilt: 3D-наклон по движению мыши ---
// На каждый mousemove считаем углы и пишем их в CSS-переменные узла —
// сам transform живёт в CSS, JS только обновляет числа через rAF.
export function useTilt({ max = 6, scale = 1.015, perspective = 800, disabled = false } = {}) {
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node || disabled) return undefined;
    if (prefersReducedMotion()) return undefined;
    if (typeof window === 'undefined' || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      return undefined;
    }

    node.style.setProperty('--tilt-perspective', `${perspective}px`);
    node.style.setProperty('--tilt-scale', String(scale));
    node.style.transformStyle = 'preserve-3d';

    let frame = 0;
    const onMove = (e) => {
      const rect = node.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      const rotY = (x - 0.5) * max * 2;
      const rotX = (0.5 - y) * max * 2;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        node.style.setProperty('--tilt-rx', `${rotX.toFixed(2)}deg`);
        node.style.setProperty('--tilt-ry', `${rotY.toFixed(2)}deg`);
        node.style.setProperty('--tilt-x', `${(x * 100).toFixed(2)}%`);
        node.style.setProperty('--tilt-y', `${(y * 100).toFixed(2)}%`);
        node.dataset.tiltActive = 'true';
      });
    };

    const onLeave = () => {
      cancelAnimationFrame(frame);
      node.style.setProperty('--tilt-rx', '0deg');
      node.style.setProperty('--tilt-ry', '0deg');
      node.dataset.tiltActive = 'false';
    };

    node.addEventListener('mousemove', onMove);
    node.addEventListener('mouseleave', onLeave);
    return () => {
      cancelAnimationFrame(frame);
      node.removeEventListener('mousemove', onMove);
      node.removeEventListener('mouseleave', onLeave);
    };
  }, [max, scale, perspective, disabled]);

  return ref;
}

// --- useMagnetic: лёгкое «магнитное» притяжение элемента к курсору ---
export function useMagnetic({ strength = 0.25, radius = 1.6 } = {}) {
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    if (prefersReducedMotion()) return undefined;
    if (typeof window === 'undefined' || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      return undefined;
    }

    let frame = 0;
    const onMove = (e) => {
      const rect = node.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = e.clientX - cx;
      const dy = e.clientY - cy;
      const dist = Math.hypot(dx, dy);
      const maxDist = Math.max(rect.width, rect.height) * radius;
      if (dist > maxDist) {
        cancelAnimationFrame(frame);
        node.style.setProperty('--mag-x', '0px');
        node.style.setProperty('--mag-y', '0px');
        return;
      }
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        node.style.setProperty('--mag-x', `${(dx * strength).toFixed(2)}px`);
        node.style.setProperty('--mag-y', `${(dy * strength).toFixed(2)}px`);
      });
    };
    const reset = () => {
      cancelAnimationFrame(frame);
      node.style.setProperty('--mag-x', '0px');
      node.style.setProperty('--mag-y', '0px');
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    node.addEventListener('mouseleave', reset);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('mousemove', onMove);
      node.removeEventListener('mouseleave', reset);
    };
  }, [strength, radius]);

  return ref;
}

// --- useParallaxY: смещение по Y в зависимости от позиции элемента на экране ---
export function useParallaxY({ speed = 0.15, max = 36 } = {}) {
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    if (prefersReducedMotion()) return undefined;
    if (typeof window === 'undefined') return undefined;

    let frame = 0;
    const update = () => {
      const rect = node.getBoundingClientRect();
      const viewportH = window.innerHeight || 0;
      // -1 (далеко вверху) -> 0 (центр) -> +1 (далеко внизу)
      const progress = (rect.top + rect.height / 2 - viewportH / 2) / (viewportH / 2 + rect.height / 2);
      const clamped = Math.max(-1.4, Math.min(1.4, progress));
      const offset = Math.max(-max, Math.min(max, clamped * speed * 100));
      node.style.setProperty('--parallax-y', `${offset.toFixed(2)}px`);
    };
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [speed, max]);

  return ref;
}

// --- useScrollProgress: значение 0..1 — сколько секции уже прокручено ---
export function useScrollProgress() {
  const ref = useRef(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    if (typeof window === 'undefined') return undefined;

    let frame = 0;
    const update = () => {
      const rect = node.getBoundingClientRect();
      const viewportH = window.innerHeight || 1;
      const total = rect.height + viewportH;
      const passed = viewportH - rect.top;
      const ratio = Math.max(0, Math.min(1, passed / total));
      node.style.setProperty('--scroll-progress', ratio.toFixed(4));
      setProgress(ratio);
    };
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return { ref, progress };
}

// --- useStaggerChildren: каждому ребёнку контейнера ставит свой --reveal-delay ---
export function useStaggerChildren({ step = 60, selector = '[data-stagger-child]' } = {}) {
  const ref = useRef(null);
  const apply = useCallback(() => {
    const root = ref.current;
    if (!root) return;
    const items = root.querySelectorAll(selector);
    items.forEach((child, idx) => {
      child.style.setProperty('--reveal-delay', `${idx * step}ms`);
    });
  }, [selector, step]);

  useEffect(() => {
    apply();
  }, [apply]);

  return ref;
}
